import { useMutation, useQuery } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { useState } from 'react'
import { FundsOriginChannelEnum } from '../../../../common/enums/fundsOriginEnum'
import {
  IManualSuscriptionContractSummary,
  IValidateManualSuscriptionIdentityRequest,
  ManualSuscriptionContractTypeEnum,
} from '../../../../common/interfaces'
import { FundsOriginService, ManualSuscriptionService } from '../../../../services'
import { notifyApiError } from '../utils/notifyApiError'

const isMancomunadoContract = (contract?: IManualSuscriptionContractSummary) =>
  contract?.tipoContrato.code === ManualSuscriptionContractTypeEnum.MANCOMUNADO_CONJUNTO ||
  contract?.tipoContrato.code === ManualSuscriptionContractTypeEnum.MANCOMUNADO_INDISTINTO

export const useManualOperations = (contractIndex: number | '') => {
  const service = ManualSuscriptionService()
  const fundsOriginService = FundsOriginService()
  const [customerId, setCustomerId] = useState<string | null>(null)
  const [contracts, setContracts] = useState<IManualSuscriptionContractSummary[]>([])

  const validateIdentityMutation = useMutation({
    mutationFn: (req: IValidateManualSuscriptionIdentityRequest) => service.validateIdentity(req),
    onSuccess: (res) => {
      setCustomerId(res.customer_id)
      setContracts(res.contracts)
    },
  })

  const selectedContract = contractIndex !== '' ? contracts[contractIndex] : undefined
  const isMancomunado = isMancomunadoContract(selectedContract)

  const contractRiskProfileQuery = useQuery({
    queryKey: [
      'manual-suscription-contract-risk-profile',
      customerId,
      selectedContract?.codParticipe,
    ],
    queryFn: () =>
      service.getContractRiskProfile(customerId as string, selectedContract!.codParticipe),
    enabled: Boolean(customerId) && Boolean(selectedContract) && isMancomunado,
    staleTime: Infinity,
    onError: (error) => {
      const axiosError = error as AxiosError<{ message?: string }>
      notifyApiError(axiosError.response?.data?.message)
    },
  })

  const riskProfileId = contractRiskProfileQuery.data?.risk_profile_id

  const fundsQuery = useQuery({
    queryKey: ['manual-suscription-funds', customerId, riskProfileId],
    queryFn: () => service.listFunds(customerId as string, riskProfileId),
    enabled:
      Boolean(customerId) &&
      (!isMancomunado || (Boolean(riskProfileId) && !contractRiskProfileQuery.isFetching)),
    staleTime: Infinity,
  })

  const amountMinInversionQuery = useQuery({
    queryKey: ['manual-suscription-amount-min-inversion', customerId],
    queryFn: () => service.getAmountMinInversion(customerId as string),
    enabled: Boolean(customerId),
  })

  const fundsOriginQuery = useQuery({
    queryKey: ['funds-origin-list', FundsOriginChannelEnum.MANUAL],
    queryFn: () => fundsOriginService.list(FundsOriginChannelEnum.MANUAL),
  })

  const viasQuery = useQuery({
    queryKey: ['manual-suscription-via-list'],
    queryFn: () => service.listVias(),
  })

  const createManualMutation = useMutation({
    mutationFn: service.createManual,
  })

  const reset = () => {
    setCustomerId(null)
    setContracts([])
    validateIdentityMutation.reset()
    createManualMutation.reset()
  }

  return {
    customerId,
    contracts,
    validateIdentityMutation,
    isMancomunado,
    riskProfileId,
    contractRiskProfileQuery,
    fundsQuery,
    amountMinInversionQuery,
    fundsOriginQuery,
    viasQuery,
    createManualMutation,
    reset,
  }
}
